import { useMemo } from "react";
import { useTellescopeQuery } from "../use-tellescope-query";
import { validateUrl } from "@tellescope/utils";

/**
 * Hook to fetch a secure file URL from a secure file name or validate an existing URL.
 *
 * This hook handles the common pattern in Tellescope where media content (avatars, images, attachments, etc.)
 * can be stored as either a direct URL or as a secure file name that needs to be fetched from the files API.
 *
 * @param fileIdentifier - Either a valid URL or a secure file name to fetch from the files API
 * @returns The file URL to use for the media src, or undefined if unavailable
 *
 * @example
 * ```tsx
 * // For avatars
 * const avatarUrl = useSecureFileUrl(user.avatar);
 * return <img src={avatarUrl} alt="Avatar" />;
 *
 * // For attachments
 * const imageUrl = useSecureFileUrl(attachment.secureName);
 * return <img src={imageUrl} alt="Attachment" />;
 * ```
 */
export const useSecureFileUrl = (fileIdentifier?: string): string | undefined => {
  // Check if fileIdentifier is a valid URL
  const isValidUrl = useMemo(() => {
    if (!fileIdentifier) return false;
    const result = validateUrl(fileIdentifier);
    return !result.error;
  }, [fileIdentifier]);

  // If it's not a valid URL, treat it as a secureName and fetch the download URL
  const { data: fileData } = useTellescopeQuery({
    model: "files",
    method: "file_download_URL",
    request: { secureName: fileIdentifier || "" },
    enabled: !!fileIdentifier && !isValidUrl,
    staleTime: 4 * 60 * 1000, // 4 minutes (presigned URLs typically valid for 5 minutes)
    retry: 1, // Fast fail for missing files
  });

  // Return the fileIdentifier if it's a URL, otherwise return the fetched download URL
  return isValidUrl ? fileIdentifier : fileData?.downloadURL;
};
